import Head from 'next/head';
import {
  DEFAULT_OG_IMAGE,
  SITE_KEYWORDS,
  SITE_LOCALE,
  SITE_NAME,
  TWITTER_HANDLE,
} from '@/lib/site';
import { absoluteUrl, formatTitle } from '@/lib/seo';

/**
 * Per-page SEO tags (title, description, canonical, Open Graph, Twitter, robots, JSON-LD).
 */
export default function SeoHead({
  title,
  description,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  nofollow = false,
  jsonLd = null,
}) {
  const pageTitle = formatTitle(title);
  const desc = (description || '').trim();
  const canonical = absoluteUrl(path);
  const ogImage = image?.startsWith('http')
    ? image
    : absoluteUrl(image || '/logo/logo-dark.png');

  const robots = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
    'max-image-preview:large',
    'max-snippet:-1',
    'max-video-preview:-1',
  ].join(', ');

  const ldNodes = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Head>
      <title>{pageTitle}</title>
      {desc ? <meta name="description" content={desc} /> : null}
      <meta name="keywords" content={SITE_KEYWORDS} />
      <meta name="author" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      {desc ? <meta property="og:description" content={desc} /> : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:image:alt" content={`${SITE_NAME} logo — ${desc.slice(0, 100) || SITE_NAME}`} />
      <meta property="og:locale" content={SITE_LOCALE} />

      <meta name="twitter:card" content="summary_large_image" />
      {TWITTER_HANDLE ? <meta name="twitter:site" content={TWITTER_HANDLE} /> : null}
      <meta name="twitter:title" content={pageTitle} />
      {desc ? <meta name="twitter:description" content={desc} /> : null}
      <meta name="twitter:image" content={ogImage} />

      {ldNodes.map((node, i) => (
        <script
          key={`jsonld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }}
        />
      ))}
    </Head>
  );
}
